import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { toast } from 'sonner';
import {
  BarChart3, ShoppingCart, Users, CreditCard, Eye,
  ChevronLeft, ChevronRight, Search, RefreshCw,
  Mail, Copy, Clock, CheckCircle, AlertTriangle,
  TrendingUp, DollarSign, Package, X, Filter,
  ArrowRight, XCircle, Globe, Smartphone, UserPlus
} from 'lucide-react';
import {
  getPurchaseDashboard, getCheckoutSessions, getCheckoutSessionDetail,
  getCompletedPurchases, getPurchasedLicenses, getSoldAddons,
  getTimbresSales, getWebPurchaseUsers, getAbandonedCarts
} from '../../services/api';

const TABS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'sesiones', label: 'Sesiones' },
  { key: 'abandonados', label: 'Abandonados' },
  { key: 'completadas', label: 'Completadas' },
  { key: 'licencias', label: 'Licencias' },
  { key: 'addons', label: 'Addons' },
  { key: 'usuarios', label: 'Usuarios' },
];

const STATUS_LABELS = {
  started: { label: 'Iniciado', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'En progreso', color: 'bg-yellow-100 text-yellow-700' },
  abandoned: { label: 'Abandonado', color: 'bg-red-100 text-red-700' },
  payment_failed: { label: 'Pago fallido', color: 'bg-red-100 text-red-700' },
  completed: { label: 'Completado', color: 'bg-green-100 text-green-700' },
};

const STEP_LABELS = {
  1: 'Tu Negocio',
  2: 'Tu Plan',
  3: 'Resumen',
  4: 'Pago',
};

const STEP_COLORS = {
  1: 'bg-red-100 text-red-700',
  2: 'bg-orange-100 text-orange-700',
  3: 'bg-yellow-100 text-yellow-700',
  4: 'bg-green-100 text-green-700',
};

const EVENT_LABELS = {
  checkout_started: 'Inicio checkout',
  step_completed: 'Paso completado',
  plan_selected: 'Plan seleccionado',
  checkout_ready: 'Listo para pagar',
  payment_attempted: 'Intento de pago',
  payment_succeeded: 'Pago exitoso',
  payment_failed: 'Pago fallido',
  checkout_abandoned: 'Abandonado',
  provisioning_completed: 'Licencia generada',
};

const formatMXN = (amount) => {
  if (amount == null) return '—';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const formatHoursAgo = (hours) => {
  if (hours == null) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
};

const StatusBadge = ({ status }) => {
  const st = STATUS_LABELS[status] || { label: status, color: 'bg-slate-100 text-slate-600' };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>;
};

const StepBadge = ({ step }) => {
  const color = STEP_COLORS[step] || 'bg-slate-100 text-slate-600';
  const label = STEP_LABELS[step] || `Paso ${step}`;
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${color}`}>{step} - {label}</span>;
};

const Pagination = ({ page, pages, total, limit, onPageChange }) => {
  if (total <= limit) return null;
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
      <span className="text-xs text-slate-500">{from}-{to} de {total}</span>
      <div className="flex gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-slate-500 px-2 py-1">{page}/{pages}</span>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= pages}
          className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const ComprasWeb = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [period, setPeriod] = useState(30);

  // Dashboard
  const [dashboard, setDashboard] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);

  // Sessions
  const [sessions, setSessions] = useState({ sessions: [], total: 0, pages: 1 });
  const [sessPage, setSessPage] = useState(1);
  const [sessStatus, setSessStatus] = useState('');
  const [sessEmail, setSessEmail] = useState('');
  const [sessLoading, setSessLoading] = useState(false);

  // Abandoned
  const [abandoned, setAbandoned] = useState({ carts: [], total: 0, pages: 1 });
  const [abPage, setAbPage] = useState(1);
  const [abMinStep, setAbMinStep] = useState(1);
  const [abHasEmail, setAbHasEmail] = useState(true);
  const [abLoading, setAbLoading] = useState(false);

  // Completed
  const [completed, setCompleted] = useState({ purchases: [], total: 0, pages: 1 });
  const [compPage, setCompPage] = useState(1);
  const [compEmail, setCompEmail] = useState('');
  const [compLoading, setCompLoading] = useState(false);

  // Licenses
  const [licenses, setLicenses] = useState({ licenses: [], total: 0, pages: 1 });
  const [licPage, setLicPage] = useState(1);
  const [licLoading, setLicLoading] = useState(false);

  // Addons
  const [addons, setAddons] = useState({ addons: [] });
  const [timbres, setTimbres] = useState({ packages: [], total_timbres_sold: 0, total_revenue: 0 });
  const [addonsLoading, setAddonsLoading] = useState(false);

  // Users
  const [users, setUsers] = useState({ users: [], total_new_users: 0 });
  const [usersPage, setUsersPage] = useState(1);
  const [usersLoading, setUsersLoading] = useState(false);

  // Detail modal
  const [detailSession, setDetailSession] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ──── Loaders ────

  const loadDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const res = await getPurchaseDashboard({ days: period });
      setDashboard(res.data);
    } catch { toast.error('Error cargando dashboard'); }
    finally { setDashLoading(false); }
  }, [period]);

  const loadSessions = useCallback(async () => {
    setSessLoading(true);
    try {
      const params = { days: period, page: sessPage, limit: 50 };
      if (sessStatus) params.status = sessStatus;
      if (sessEmail) params.email = sessEmail;
      const res = await getCheckoutSessions(params);
      setSessions(res.data);
    } catch { toast.error('Error cargando sesiones'); }
    finally { setSessLoading(false); }
  }, [period, sessPage, sessStatus, sessEmail]);

  const loadAbandoned = useCallback(async () => {
    setAbLoading(true);
    try {
      const res = await getAbandonedCarts({ days: period, min_step: abMinStep, has_email: abHasEmail, page: abPage, limit: 50 });
      setAbandoned(res.data);
    } catch { toast.error('Error cargando abandonados'); }
    finally { setAbLoading(false); }
  }, [period, abPage, abMinStep, abHasEmail]);

  const loadCompleted = useCallback(async () => {
    setCompLoading(true);
    try {
      const params = { days: period, page: compPage, limit: 50 };
      if (compEmail) params.email = compEmail;
      const res = await getCompletedPurchases(params);
      setCompleted(res.data);
    } catch { toast.error('Error cargando completadas'); }
    finally { setCompLoading(false); }
  }, [period, compPage, compEmail]);

  const loadLicenses = useCallback(async () => {
    setLicLoading(true);
    try {
      const res = await getPurchasedLicenses({ days: period, page: licPage, limit: 50 });
      setLicenses(res.data);
    } catch { toast.error('Error cargando licencias'); }
    finally { setLicLoading(false); }
  }, [period, licPage]);

  const loadAddons = useCallback(async () => {
    setAddonsLoading(true);
    try {
      const [aRes, tRes] = await Promise.all([
        getSoldAddons({ days: period }),
        getTimbresSales({ days: period }),
      ]);
      setAddons(aRes.data);
      setTimbres(tRes.data);
    } catch { toast.error('Error cargando addons'); }
    finally { setAddonsLoading(false); }
  }, [period]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const res = await getWebPurchaseUsers({ days: period, page: usersPage, limit: 50 });
      setUsers(res.data);
    } catch { toast.error('Error cargando usuarios'); }
    finally { setUsersLoading(false); }
  }, [period, usersPage]);

  const openDetail = async (sessionId) => {
    setDetailLoading(true);
    try {
      const res = await getCheckoutSessionDetail(sessionId);
      setDetailSession(res.data);
    } catch { toast.error('Error cargando detalle'); }
    finally { setDetailLoading(false); }
  };

  // ──── Effects ────

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  useEffect(() => {
    if (activeTab === 'sesiones') loadSessions();
    else if (activeTab === 'abandonados') loadAbandoned();
    else if (activeTab === 'completadas') loadCompleted();
    else if (activeTab === 'licencias') loadLicenses();
    else if (activeTab === 'addons') loadAddons();
    else if (activeTab === 'usuarios') loadUsers();
    else if (activeTab === 'dashboard') loadDashboard();
  }, [activeTab, loadSessions, loadAbandoned, loadCompleted, loadLicenses, loadAddons, loadUsers, loadDashboard]);

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    toast.success('Email copiado');
  };

  // ──── Dashboard Tab ────

  const renderDashboard = () => {
    if (dashLoading || !dashboard) {
      return <div className="flex items-center justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>;
    }
    const d = dashboard;
    const sc = d.status_counts || {};
    const totalAbandon = d.abandonment_by_step?.reduce((s, x) => s + x.count, 0) || 0;

    return (
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <KPICard icon={Globe} label="Sesiones" value={d.total_sessions} color="text-blue-600" bg="bg-blue-50" />
          <KPICard icon={TrendingUp} label="Conversion" value={`${d.conversion_rate}%`}
            color={d.conversion_rate > 5 ? 'text-green-600' : d.conversion_rate > 2 ? 'text-yellow-600' : 'text-red-600'}
            bg={d.conversion_rate > 5 ? 'bg-green-50' : d.conversion_rate > 2 ? 'bg-yellow-50' : 'bg-red-50'} />
          <KPICard icon={CheckCircle} label="Completadas" value={d.completed_purchases} color="text-green-600" bg="bg-green-50" />
          <KPICard icon={XCircle} label="Pagos fallidos" value={d.failed_payments} color="text-red-600" bg="bg-red-50" />
          <KPICard icon={DollarSign} label="Ingresos" value={formatMXN(d.total_revenue)} color="text-teal-600" bg="bg-teal-50" />
          <KPICard icon={ShoppingCart} label="Abandonados" value={totalAbandon} color="text-orange-600" bg="bg-orange-50" />
        </div>

        {/* Funnel */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-teal-700" /> Funnel de Conversion
          </h3>
          <FunnelChart
            total={d.total_sessions}
            steps={[
              { label: 'Step 1 — Tu Negocio', value: d.total_sessions },
              { label: 'Step 2 — Tu Plan', value: d.total_sessions - (d.abandonment_by_step?.[0]?.count || 0) },
              { label: 'Step 3 — Resumen', value: sc.in_progress + sc.completed + (sc.payment_failed || 0) + (d.abandonment_by_step?.[2]?.count || 0) + (d.abandonment_by_step?.[3]?.count || 0) },
              { label: 'Step 4 — Pago', value: sc.completed + (sc.payment_failed || 0) + (d.abandonment_by_step?.[3]?.count || 0) },
              { label: 'Completado', value: sc.completed },
            ]}
          />
        </div>

        {/* Products & Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-teal-700" /> Productos vendidos
            </h3>
            {Object.keys(d.products_sold || {}).length === 0
              ? <p className="text-sm text-slate-400">Sin datos en este periodo</p>
              : <div className="space-y-3">
                  {Object.entries(d.products_sold).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
                    <div key={name} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700 capitalize">{name}</span>
                      <span className="text-sm font-bold text-slate-900">{count}</span>
                    </div>
                  ))}
                </div>
            }
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-teal-700" /> Planes vendidos
            </h3>
            {Object.keys(d.plans_sold || {}).length === 0
              ? <p className="text-sm text-slate-400">Sin datos en este periodo</p>
              : <div className="space-y-3">
                  {Object.entries(d.plans_sold).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
                    <div key={name} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700 capitalize">{name}</span>
                      <span className="text-sm font-bold text-slate-900">{count}</span>
                    </div>
                  ))}
                </div>
            }
          </div>
        </div>
      </div>
    );
  };

  // ──── Sessions Tab ────

  const renderSessions = () => (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Globe className="w-5 h-5 text-teal-700" /> Sesiones de Checkout
          </h2>
          <div className="flex flex-wrap gap-2">
            <select value={sessStatus} onChange={e => { setSessStatus(e.target.value); setSessPage(1); }}
              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5">
              <option value="">Todos los estados</option>
              <option value="started">Iniciado</option>
              <option value="in_progress">En progreso</option>
              <option value="abandoned">Abandonado</option>
              <option value="payment_failed">Pago fallido</option>
              <option value="completed">Completado</option>
            </select>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2 text-slate-400" />
              <input type="text" placeholder="Buscar email..." value={sessEmail}
                onChange={e => { setSessEmail(e.target.value); setSessPage(1); }}
                className="text-sm border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 w-44" />
            </div>
          </div>
        </div>
      </div>
      {sessLoading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : sessions.sessions.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">No hay sesiones en este periodo</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Fecha</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Negocio</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Producto</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Plan</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Monto</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Paso</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Estado</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sessions.sessions.map(s => (
                  <tr key={s.session_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{formatDate(s.started_at)}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{s.customer_email || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{s.business_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 capitalize">{s.product || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 capitalize">{s.plan_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 text-right">{formatMXN(s.estimated_total)}</td>
                    <td className="px-4 py-3 text-center"><StepBadge step={s.last_step} /></td>
                    <td className="px-4 py-3 text-center"><StatusBadge status={s.status} /></td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => openDetail(s.session_id)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-teal-700">
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={sessions.page} pages={sessions.pages} total={sessions.total} limit={50} onPageChange={setSessPage} />
        </>
      )}
    </div>
  );

  // ──── Abandoned Tab ────

  const renderAbandoned = () => (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-orange-600" /> Carritos Abandonados
          </h2>
          <div className="flex flex-wrap gap-2 items-center">
            <label className="flex items-center gap-1.5 text-sm text-slate-600">
              <Filter className="w-3.5 h-3.5" /> Paso min:
              <select value={abMinStep} onChange={e => { setAbMinStep(Number(e.target.value)); setAbPage(1); }}
                className="text-sm border border-slate-200 rounded-lg px-2 py-1">
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
                <option value={4}>4</option>
              </select>
            </label>
            <label className="flex items-center gap-1.5 text-sm text-slate-600">
              <input type="checkbox" checked={abHasEmail} onChange={e => { setAbHasEmail(e.target.checked); setAbPage(1); }}
                className="rounded border-slate-300 text-teal-600" />
              Solo con email
            </label>
          </div>
        </div>
      </div>
      {abLoading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : abandoned.carts.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">No hay carritos abandonados</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Hace</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Nombre</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Negocio</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Producto</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Plan</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Monto est.</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Paso</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Estado</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {abandoned.carts.map(c => (
                  <tr key={c.session_id} className={`hover:bg-slate-50 ${!c.customer_email ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{formatHoursAgo(c.hours_ago)}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">{c.customer_email || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{c.customer_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{c.business_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 capitalize">{c.product || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 capitalize">{c.plan_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 text-right">{formatMXN(c.estimated_total)}</td>
                    <td className="px-4 py-3 text-center"><StepBadge step={c.last_step} /></td>
                    <td className="px-4 py-3 text-center"><StatusBadge status={c.status} /></td>
                    <td className="px-4 py-3 text-center">
                      {c.customer_email && (
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => copyEmail(c.customer_email)} title="Copiar email"
                            className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-teal-700">
                            <Copy className="w-4 h-4" />
                          </button>
                          <a href={`mailto:${c.customer_email}`} title="Enviar email"
                            className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-teal-700">
                            <Mail className="w-4 h-4" />
                          </a>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={abandoned.page} pages={abandoned.pages} total={abandoned.total} limit={50} onPageChange={setAbPage} />
        </>
      )}
    </div>
  );

  // ──── Completed Tab ────

  const renderCompleted = () => (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" /> Compras Completadas
        </h2>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2 text-slate-400" />
          <input type="text" placeholder="Buscar email..." value={compEmail}
            onChange={e => { setCompEmail(e.target.value); setCompPage(1); }}
            className="text-sm border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 w-44" />
        </div>
      </div>
      {compLoading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : completed.purchases.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">No hay compras completadas</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Fecha</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Negocio</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Producto</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Plan</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Ciclo</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Monto</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Licencia</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {completed.purchases.map(p => (
                  <tr key={p.session_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap">{formatDate(p.completed_at)}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{p.customer_email || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{p.business_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 capitalize">{p.product || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 capitalize">{p.plan_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 capitalize">{p.billing_cycle || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 text-right font-medium">{formatMXN(p.estimated_total)}</td>
                    <td className="px-4 py-3 text-sm text-teal-700 font-mono">{p.license_serial || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => openDetail(p.session_id)} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-teal-700">
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={completed.page} pages={completed.pages} total={completed.total} limit={50} onPageChange={setCompPage} />
        </>
      )}
    </div>
  );

  // ──── Licenses Tab ────

  const renderLicenses = () => (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-teal-700" /> Licencias Vendidas (web)
        </h2>
      </div>
      {licLoading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : licenses.licenses.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">No hay licencias de venta web en este periodo</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Serial</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Tipo</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Plan</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Estado</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Creada</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Vencimiento</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {licenses.licenses.map(l => (
                  <tr key={l.serial_number || l.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-mono text-teal-700">{l.serial_number}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 capitalize">{l.license_type || l.pos_type || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 capitalize">{l.plan || '—'}</td>
                    <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    <td className="px-4 py-3 text-sm text-slate-600">{formatDate(l.created_at)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{formatDate(l.valid_until)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={licenses.page} pages={licenses.pages} total={licenses.total} limit={50} onPageChange={setLicPage} />
        </>
      )}
    </div>
  );

  // ──── Addons Tab ────

  const renderAddons = () => (
    <div className="space-y-6">
      {/* Addons */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Package className="w-5 h-5 text-teal-700" /> Addons Vendidos
          </h2>
        </div>
        {addonsLoading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : addons.addons.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-sm">Sin addons vendidos en este periodo</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Tipo</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Cantidad</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {addons.addons.map(a => (
                  <tr key={a.type} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-medium text-slate-700 capitalize">{a.type}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 text-right">{a.count}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 text-right font-medium">{formatMXN(a.total_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Timbres */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-teal-700" /> Timbres Vendidos
          </h2>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-slate-500">Total timbres: <strong className="text-slate-900">{timbres.total_timbres_sold}</strong></span>
            <span className="text-slate-500">Revenue: <strong className="text-teal-700">{formatMXN(timbres.total_revenue)}</strong></span>
          </div>
        </div>
        {addonsLoading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : timbres.packages.length === 0 ? (
          <div className="text-center py-12 text-slate-400 text-sm">Sin timbres vendidos en este periodo</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Paquete</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Ventas</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Timbres</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {timbres.packages.map(p => (
                  <tr key={p.package} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-medium text-slate-700 capitalize">{p.package}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 text-right">{p.count}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 text-right">{p.total_timbres}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 text-right font-medium">{formatMXN(p.total_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // ──── Users Tab ────

  const renderUsers = () => (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-teal-700" /> Usuarios Creados (compra web)
        </h2>
        <span className="text-sm text-slate-500">Total: <strong>{users.total_new_users}</strong></span>
      </div>
      {usersLoading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : users.users.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">No hay usuarios de compra web en este periodo</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Nombre</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Negocio</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Rol</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Creado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.users.map(u => (
                  <tr key={u.id || u.user_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-700">{u.full_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{u.email}</td>
                    <td className="px-4 py-3 text-sm text-slate-700">{u.business_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-700 capitalize">{u.role || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{formatDate(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={users.page || 1} pages={Math.ceil(users.total_new_users / 50) || 1} total={users.total_new_users} limit={50} onPageChange={setUsersPage} />
        </>
      )}
    </div>
  );

  // ──── Detail Modal ────

  const renderDetailModal = () => {
    if (!detailSession && !detailLoading) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setDetailSession(null)}>
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          {detailLoading ? (
            <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : detailSession && (
            <>
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Detalle de Sesion</h2>
                  <p className="text-xs text-slate-400 font-mono mt-1">{detailSession.session_id}</p>
                </div>
                <button onClick={() => setDetailSession(null)} className="text-slate-400 hover:text-slate-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                {/* Status */}
                <div className="flex items-center gap-3">
                  <StatusBadge status={detailSession.status} />
                  <StepBadge step={detailSession.last_step} />
                </div>

                {/* Customer Info */}
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="Email" value={detailSession.customer_email} />
                  <InfoField label="Nombre" value={detailSession.customer_name} />
                  <InfoField label="Negocio" value={detailSession.business_name} />
                  <InfoField label="Tipo negocio" value={detailSession.business_type} />
                </div>

                {/* Plan Info */}
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="Producto" value={detailSession.product} capitalize />
                  <InfoField label="Plan" value={detailSession.plan_name} capitalize />
                  <InfoField label="Ciclo" value={detailSession.billing_cycle} capitalize />
                  <InfoField label="Monto estimado" value={formatMXN(detailSession.estimated_total)} />
                </div>

                {/* Addons */}
                {detailSession.addons?.length > 0 && (
                  <div>
                    <span className="text-xs font-semibold text-slate-500 uppercase">Addons</span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {detailSession.addons.map(a => (
                        <span key={a} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">{a}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="IP" value={detailSession.client_ip} />
                  <InfoField label="Referrer" value={detailSession.referrer || 'Directo'} />
                  {detailSession.license_serial && <InfoField label="Licencia" value={detailSession.license_serial} mono />}
                  {detailSession.payment_id && <InfoField label="Payment ID" value={detailSession.payment_id} mono />}
                </div>

                {/* Timeline */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Timeline de Eventos</h3>
                  <div className="space-y-0">
                    {(detailSession.timeline || []).map((t, i) => (
                      <div key={i} className="flex items-start gap-3 relative">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-1.5 ${
                            t.event === 'payment_succeeded' || t.event === 'provisioning_completed' ? 'bg-green-500' :
                            t.event === 'payment_failed' || t.event === 'checkout_abandoned' ? 'bg-red-500' :
                            'bg-teal-500'
                          }`} />
                          {i < (detailSession.timeline || []).length - 1 && (
                            <div className="w-px h-full bg-slate-200 min-h-[24px]" />
                          )}
                        </div>
                        <div className="pb-4 flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-slate-900">{EVENT_LABELS[t.event] || t.event}</span>
                            {t.step && <span className="text-xs text-slate-400">Paso {t.step}</span>}
                          </div>
                          <span className="text-xs text-slate-400">{formatDate(t.timestamp)}</span>
                          {t.metadata && Object.keys(t.metadata).length > 0 && (
                            <div className="mt-1 text-xs text-slate-500 bg-slate-50 rounded px-2 py-1">
                              {Object.entries(t.metadata).map(([k, v]) => (
                                <span key={k} className="mr-3">{k}: <strong>{String(v)}</strong></span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* User Agent */}
                {detailSession.user_agent && (
                  <div>
                    <span className="text-xs font-semibold text-slate-500 uppercase">User Agent</span>
                    <p className="text-xs text-slate-400 mt-0.5 break-all">{detailSession.user_agent}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ──── Main Render ────

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Compras Web</h1>
            <p className="text-sm text-slate-500 mt-1">Tracking del funnel de compra desde enlocal.com.mx</p>
          </div>
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            {[7, 30, 90, 365].map(d => (
              <button key={d} onClick={() => setPeriod(d)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  period === d ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}>
                {d}d
              </button>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit overflow-x-auto">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'sesiones' && renderSessions()}
        {activeTab === 'abandonados' && renderAbandoned()}
        {activeTab === 'completadas' && renderCompleted()}
        {activeTab === 'licencias' && renderLicenses()}
        {activeTab === 'addons' && renderAddons()}
        {activeTab === 'usuarios' && renderUsers()}
      </div>

      {/* Detail Modal */}
      {renderDetailModal()}
    </DashboardLayout>
  );
};

// ──── Sub-components ────

const KPICard = ({ icon: Icon, label, value, color, bg }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${bg}`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-lg font-bold text-slate-900 truncate">{value}</p>
      </div>
    </div>
  </div>
);

const FunnelChart = ({ total, steps }) => {
  if (!total || total === 0) return <p className="text-sm text-slate-400">Sin datos</p>;
  return (
    <div className="space-y-3">
      {steps.map((step, i) => {
        const pct = total > 0 ? Math.round((step.value / total) * 100) : 0;
        const width = Math.max(pct, 2);
        return (
          <div key={i} className="flex items-center gap-3">
            <span className="text-sm text-slate-600 w-40 flex-shrink-0">{step.label}</span>
            <div className="flex-1 bg-slate-100 rounded-full h-6 relative overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-teal-500 to-teal-600 transition-all duration-500"
                style={{ width: `${width}%` }} />
              <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-slate-700">
                {step.value} ({pct}%)
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const InfoField = ({ label, value, capitalize, mono }) => (
  <div>
    <span className="text-xs font-semibold text-slate-500 uppercase">{label}</span>
    <p className={`text-sm text-slate-900 mt-0.5 ${capitalize ? 'capitalize' : ''} ${mono ? 'font-mono text-teal-700' : ''}`}>
      {value || '—'}
    </p>
  </div>
);

export default ComprasWeb;
